import { TextField } from '@origin-digital/style-guide';
import { SearchIcon } from '@origin-digital/icon';
//@ts-ignore
import { AddressLookup, QAS_PROFILES } from '@origin-digital/utils/dist/cjs/qas-lookup';
import axios from 'axios';
import * as React from 'react';

import {
  AddressLookupService,
  QASAddressLookUpObject,
  QASAddressLookUpProps,
  QASAddressLookUpState,
  QASSearchResult
} from './QASAddressLookUp.types';

import * as Utils from './helpers/QASAddressLookUp.helper';
import Suggestions from './Suggestions';

class QASAddressLookUp extends React.Component<QASAddressLookUpProps, QASAddressLookUpState> {
  addressLookupService: AddressLookupService;
  timeout: NodeJS.Timer | null;

  static getDerivedStateFromProps(
    nextProps: QASAddressLookUpProps,
    prevState: QASAddressLookUpState
  ): Partial<QASAddressLookUpState | null> {
    if (nextProps.asyncValidating !== prevState.asyncValidating) {
      return { asyncValidating: nextProps.asyncValidating };
    }

    if (nextProps.showManualAddress !== prevState.showManualAddress) {
      return { showManualAddress: nextProps.showManualAddress };
    } else return null;
  }

  constructor(props: QASAddressLookUpProps) {
    super(props);

    this.state = {
      query: '',
      qasSearchResult: null,
      suggestions: [],
      asyncValidating: false,
      showManualAddress: false
    };

    this.addressLookupService = AddressLookup({
      ajaxExecutor: axios,
      authToken: this.props.qasAuthToken
    });

    this.timeout = null;
  }

  componentDidMount() {
    const { value, showManualAddress } = this.props;
    if (!showManualAddress) {
      this.setAddress(value);
    }
  }

  componentDidUpdate(prevProps: QASAddressLookUpProps, prevState: QASAddressLookUpState) {
    const { asyncValidating, showManualAddress } = this.state;

    if (prevProps.asyncValidating !== prevState.asyncValidating) {
      this.setState({
        asyncValidating
      });
    }

    if (prevProps.showManualAddress !== prevState.showManualAddress) {
      this.setState({
        showManualAddress
      });
    }
  }

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
  }

  onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { onBlur } = this.props;

    if (onBlur) onBlur(event);
  };

  onChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { onChange } = this.props;
    const query = event.currentTarget.value;

    onChange(undefined);
    this.setState({ query }, () => {
      if (Utils.isQuerySearchable(query)) {
        this.getMatchingAddresses(query);
      } else {
        this.removeSuggestions();
      }
    });
  };

  onClick = (_event: React.MouseEvent<HTMLDivElement>, result: QASSearchResult): void => {
    const { onAddressSelect, onEnterAddressManually } = this.props;

    if (result.addressId) {
      onAddressSelect(result);
      this.setState(
        {
          qasSearchResult: result,
          query: result.label,
          suggestions: []
        },
        () => {
          this.parseAddress(result.addressId);
        }
      );
    } else {
      this.setState({
        query: '',
        suggestions: []
      });
      onEnterAddressManually();
    }
  };

  getMatchingAddresses = (query: string): void => {
    const { qasProfile, onError, allowManualAddressEntry, manualAddressPlaceholder } = this.props;

    if (this.timeout) clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      const MAX_RESULTS = 10;

      this.addressLookupService
        .getMatchingAddresses(query, QAS_PROFILES[qasProfile])
        .then(response => {
          let suggestions: any[] = response.slice(0, MAX_RESULTS);
          if (suggestions.length <= 3 && allowManualAddressEntry) {
            suggestions.unshift({
              addressId: null,
              label: manualAddressPlaceholder,
              postcode: undefined
            });
          }
          this.setState({
            suggestions
          });
        })
        .catch(error => {
          if (onError) onError(error, 'QASAddressLookUp.getMatchingAddresses');
        });
    }, 400);
  };

  parseAddress = (addressId: string): void => {
    const { onChange, onError } = this.props;
    const { qasSearchResult } = this.state;

    this.addressLookupService
      .parseAddress(addressId)
      .then(response => {
        onChange({
          qasAddress: response,
          qasSearchResult: qasSearchResult
        });
      })
      .catch(error => {
        if (onError) onError(error, 'QASAddressLookUp.parseAddress');
      });
  };

  removeSuggestions = (): void => {
    this.setState({ suggestions: [] });
  };

  setAddress = (address?: QASAddressLookUpObject | string): void => {
    const { onChange } = this.props;

    if (address !== undefined && typeof address !== 'string') {
      this.setState(
        {
          query: Utils.getQueryValue(address),
          qasSearchResult: address.qasSearchResult
        },
        () => {
          onChange({
            qasAddress: address.qasAddress,
            qasSearchResult: address.qasSearchResult
          });
        }
      );
    }
  };

  suggestionDropDown = (): JSX.Element => {
    const { query, suggestions } = this.state;

    return !suggestions.length ? (
      <React.Fragment />
    ) : (
      <Suggestions suggestions={suggestions} query={query} onClick={this.onClick} />
    );
  };

  render(): JSX.Element {
    const {
      disabled,
      error,
      label,
      name,
      width,
      touched,
      children,
      manualAddressPlaceholder,
      'data-id': dataId
    } = this.props;
    const { query, asyncValidating, showManualAddress } = this.state;
    const showError = disabled ? '' : error;
    const labelType = showManualAddress ? manualAddressPlaceholder : label;

    return (
      <React.Fragment>
        <TextField
          type="text"
          name={name}
          value={query}
          label={labelType}
          icon={SearchIcon}
          error={showError}
          data-id={dataId}
          touched={touched}
          disabled={disabled}
          spinner={asyncValidating}
          autoComplete="off"
          onChange={this.onChange}
          onBlur={this.onBlur}
          width={width}
          renderOverlay={this.suggestionDropDown}
        />
        {showManualAddress && <React.Fragment>{children}</React.Fragment>}
      </React.Fragment>
    );
  }
}

export { QASAddressLookUp as default };
